
import { DestroyRef, inject, Injectable, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FuseSplashScreenService {
    private _document = inject(DOCUMENT);
    private _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _hidden = new BehaviorSubject<boolean>(
        !this._document.querySelector('fuse-splash-screen')
    );
    /** Emits true only after the splash is fully invisible, not when fading starts. */
    readonly hidden$ = this._hidden.asObservable();
    private _cancelHide?: () => void;
    private _logoIntroClaimed = false;

    /**
     * Constructor
     */
    constructor() {
        this._destroyRef.onDestroy(() => {
            this._cancelHide?.();
            this._hidden.complete();
        });
        if (this._document.body.classList.contains('fuse-splash-screen-hidden')) {
            this.hide();
        }
        // Hide it on the first NavigationEnd event
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                take(1),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(() => {
                this.hide();
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the splash screen
     */
    show(): void {
        this._cancelHide?.();
        this._hidden.next(false);
        this._document.body.classList.remove('fuse-splash-screen-hidden');
    }

    /**
     * Hide the splash screen
     */
    hide(): void {
        // Repeated navigation/hide calls must not restart the fade or its fallback.
        if (this._cancelHide) return;
        const splash = this._document.querySelector<HTMLElement>('fuse-splash-screen');
        const view = this._document.defaultView;
        if (!splash || !view || this._hidden.value) {
            this._document.body.classList.add('fuse-splash-screen-hidden');
            this._hidden.next(true);
            return;
        }

        // Flush the visible state before changing classes, including very fast boots.
        const style = view.getComputedStyle(splash);
        const properties = style.transitionProperty.split(',').map(value => value.trim());
        const toMs = (value: string): number =>
            (parseFloat(value) || 0) * (value.trim().endsWith('ms') ? 1 : 1000);
        const durations = style.transitionDuration.split(',').map(toMs);
        const delays = style.transitionDelay.split(',').map(toMs);
        const duration = Math.max(0, ...properties.map((property, index) =>
            property === 'opacity' || property === 'all'
                ? durations[index % durations.length] + delays[index % delays.length]
                : 0
        ));
        let timer: ReturnType<typeof setTimeout> | undefined;
        const cleanup = (): void => {
            splash.removeEventListener('transitionend', onEnd);
            if (timer !== undefined) clearTimeout(timer);
            this._cancelHide = undefined;
        };
        const finish = (): void => {
            cleanup();
            if (this._document.body.classList.contains('fuse-splash-screen-hidden')) {
                this._hidden.next(true);
            }
        };
        const onEnd = (event: TransitionEvent): void => {
            if (event.target === splash && event.propertyName === 'opacity') finish();
        };
        this._cancelHide = cleanup;
        splash.addEventListener('transitionend', onEnd);
        this._document.body.classList.add('fuse-splash-screen-hidden');
        if (duration === 0 || style.display === 'none') {
            finish();
        } else {
            // transitionend may not fire in background tabs or for detached elements.
            timer = setTimeout(finish, duration + 50);
        }
    }

    /** Root service lifetime = page lifetime. Layout remounts render a static sign. */
    claimLogoIntro(): boolean {
        if (!this._hidden.value || this._logoIntroClaimed) return false;
        this._logoIntroClaimed = true;
        return true;
    }
}
